import { logger } from '@gem/modules/logger';
import { isFileReadable } from '@gem/utils';

import { getConfig } from './config';
import {
  HttpErrorCode,
  IUploadResult,
  IUploadUrlData,
  isFormDataConfig,
  isPresignedUrlConfig,
} from './types';
import { UploadError } from './uploadError';

const xhrCache: Record<string, XMLHttpRequest> = {};

export async function getUploadUrlData(
  fileName: string,
  mimeType: string
): Promise<IUploadUrlData> {
  const config = getConfig();

  if (isPresignedUrlConfig(config)) {
    const data = await config.getUploadUrlData({ fileName, mimeType });
    if (!data.uploadUrl) {
      throw new Error('uploadUrl not received');
    }
    return data;
  }

  const { url } = await config.getUploadUrl('userFile');
  if (!url) {
    throw new Error('uploadUrl not received');
  }
  return {
    uploadUrl: url,
    publicUrl: '',
    headers: {},
  };
}

async function uploadFilePresigned(
  url: string,
  file: File | Blob,
  fileName: string,
  headers: Record<string, string | undefined>,
  onProgress?: XMLHttpRequest['upload']['onprogress']
): Promise<void> {
  const isReadable = await isFileReadable(file);

  return new Promise((resolve, reject): void => {
    if (!isReadable) {
      reject(new Error('File is not readable'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('put', url);

    Object.keys(headers).forEach((headerName): void => {
      const value = headers[headerName];
      if (value !== undefined) {
        xhr.setRequestHeader(headerName, value);
      }
    });

    xhr.onload = function (): void {
      if (xhr.readyState === xhr.DONE && xhr.status === 200) {
        resolve();
      } else {
        reject(new UploadError(`Upload failed with status ${xhr.status}`, xhr.status));
      }
    };

    xhr.onerror = (): void => {
      reject(new UploadError(`Network request error`, HttpErrorCode.UnknownError));
    };
    xhr.onabort = reject;
    if (onProgress) {
      xhr.upload.onprogress = onProgress;
    }

    xhr.send(file);

    xhrCache[url] = xhr;
  });
}

async function uploadFileFormData(
  url: string,
  file: File | Blob,
  fileName: string,
  onProgress?: XMLHttpRequest['upload']['onprogress']
): Promise<string> {
  const config = getConfig();
  if (!isFormDataConfig(config)) {
    throw new Error('uploadFileFormData requires form-data strategy');
  }

  const isReadable = await isFileReadable(file);

  const formData = new FormData();

  if (file instanceof File) {
    formData.append('userFile', file);
  } else {
    formData.append('blob', file, fileName);
  }

  return new Promise((resolve, reject): void => {
    if (!isReadable) {
      reject(new Error('File is not readable'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('post', url);

    const hashFn = config.hashFileName ?? ((name: string): string => name);
    xhr.setRequestHeader('Content-Disposition', `attachment; filename="${hashFn(fileName || '')}"`);

    xhr.onload = function (): void {
      if (xhr.readyState === xhr.DONE) {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText) as Array<{ url: string }>;
            resolve(response[0].url);
          } catch (error) {
            logger.get('API').error('api.upload', error);
            reject(error);
          }
        } else {
          reject(new UploadError(`Upload failed with status ${xhr.status}`, xhr.status));
        }
      }
    };

    xhr.onerror = (): void => {
      reject(new UploadError(`Network request error`, HttpErrorCode.UnknownError));
    };
    xhr.onabort = reject;
    if (onProgress) {
      xhr.upload.onprogress = onProgress;
    }
    xhr.send(formData);

    xhrCache[url] = xhr;
  });
}

export async function uploadFile(
  url: string,
  file: File | Blob,
  fileName: string,
  headers: Record<string, string | undefined> | null = null,
  onProgress?: XMLHttpRequest['upload']['onprogress']
): Promise<void | Array<{ url: string }>> {
  const config = getConfig();

  if (isPresignedUrlConfig(config)) {
    await uploadFilePresigned(url, file, fileName, headers ?? {}, onProgress);
    return;
  }

  const publicUrl = await uploadFileFormData(url, file, fileName, onProgress);
  return [{ url: publicUrl }];
}

export async function uploadFileWithUrl(
  file: File | Blob,
  fileName: string,
  mimeType: string,
  onUploadStart?: (uploadUrl: string) => void,
  onProgress?: XMLHttpRequest['upload']['onprogress']
): Promise<IUploadResult> {
  const config = getConfig();

  if (isPresignedUrlConfig(config)) {
    const { uploadUrl, publicUrl, headers } = await config.getUploadUrlData({ fileName, mimeType });
    onUploadStart?.(uploadUrl);
    await uploadFilePresigned(uploadUrl, file, fileName, headers, onProgress);
    return { publicUrl, uploadUrl };
  }

  const { url } = await config.getUploadUrl('userFile');
  onUploadStart?.(url);
  const publicUrl = await uploadFileFormData(url, file, fileName, onProgress);
  return { publicUrl, uploadUrl: url };
}

async function urlToFile(url: string, filename: string, mimeType: string): Promise<File> {
  const response = await fetch(url);
  const buffer = await response.arrayBuffer();

  return new File([buffer], filename, { type: mimeType });
}

export async function uploadBase64(base64: string, mimeType = 'image/png'): Promise<string> {
  const rawData = base64.split(',');
  const detectedMimeType = rawData[0].replace('data:', '').replace(';base64', '');

  const fileName = 'upload-file-' + Date.now() + '.' + detectedMimeType.split('/')[0];

  const file = await urlToFile(base64, fileName, mimeType);

  const result = await uploadFileWithUrl(file, fileName, mimeType);
  return result.publicUrl;
}

export function cancelUploadFile(url: string): void {
  const xhr = xhrCache[url];

  if (xhr) {
    xhr.abort();
    delete xhrCache[url];
  }
}

export function dataURItoBlob(dataURI: string, option = { type: 'image/jpeg' }): Blob {
  const array = atob(dataURI.split(',')[1])
    .split('')
    .map((char) => char.charCodeAt(0));

  return new Blob([new Uint8Array(array)], option);
}

export async function readFileToBlob(file: File): Promise<File | Blob> {
  return await new Promise((resolve): void => {
    try {
      const reader = new FileReader();

      reader.onload = (): void => {
        const arrayBuffer = reader.result;
        if (arrayBuffer) {
          const blob = new Blob([arrayBuffer], { type: file.type });
          resolve(blob);
        }
        resolve(file);
      };

      reader.readAsArrayBuffer(file);
    } catch (e) {
      logger.get('SYSTEM').error('readFileToBlob ', e);
      resolve(file);
    }
  });
}

export default {
  getUploadUrlData,
  uploadFile,
  uploadFileWithUrl,
  uploadBase64,
  cancelUploadFile,
  dataURItoBlob,
  readFileToBlob,
};
